import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { HomeLink } from "../components/HomeLink";
import { ProfileAvatar } from "../components/ProfileAvatar";
import { ProviderBadge } from "../components/ProviderBadge";
import { ThemeToggle } from "../components/ThemeToggle";
import { Button } from "../components/ui/Button";
import {
  authClient,
  deleteIdentity,
  reauthenticateForDeletion,
  ReauthenticationRequiredError,
  signOutToGuest,
  unlinkProvider,
  type AuthIdentity,
} from "../lib/auth";
import { useNavigate } from "../lib/router-context";
import { useAppStore } from "../lib/store";
import { getThemeColors } from "../lib/theme";

type SocialProvider = "twitch" | "discord";

export function AccountPage() {
  const identity = useAppStore((state) => state.auth);
  const setIdentity = useAppStore((state) => state.setAuthIdentity);
  const isDark = useAppStore((state) => state.isDark);
  const toggleTheme = useAppStore((state) => state.toggleTheme);
  const theme = getThemeColors(isDark);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(
    () => new URLSearchParams(window.location.search).get("delete") === "1",
  );

  useEffect(() => {
    if (identity?.isAnonymous && pending === null) {
      navigate(`/login${window.location.search}`);
    }
  }, [identity, pending]);

  if (!identity) return null;

  const run = async (
    key: string,
    action: () => Promise<AuthIdentity>,
  ): Promise<boolean> => {
    setPending(key);
    setError(null);
    try {
      setIdentity(await action());
      return true;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t("account.error"));
      return false;
    } finally {
      setPending(null);
    }
  };

  const connect = async (provider: SocialProvider) => {
    setPending(provider);
    setError(null);
    const result = await authClient.linkSocial({
      provider,
      callbackURL: `${window.location.origin}/account`,
      errorCallbackURL: `${window.location.origin}/account`,
    });
    if (result?.error) {
      setError(result.error.message || t("account.error"));
      setPending(null);
    }
  };

  const providerLabel = (provider: SocialProvider) =>
    provider === "twitch" ? t("account.twitch") : t("account.discord");

  const confirmAccountDeletion = async () => {
    setPending("delete");
    setError(null);
    try {
      setIdentity(await deleteIdentity(identity));
      navigate("/");
    } catch (caught) {
      if (caught instanceof ReauthenticationRequiredError) {
        try {
          await reauthenticateForDeletion(identity);
          return;
        } catch (reauthError) {
          setError(
            reauthError instanceof Error
              ? reauthError.message
              : t("account.error"),
          );
        }
      } else {
        setError(caught instanceof Error ? caught.message : t("account.error"));
      }
    } finally {
      setPending(null);
    }
  };

  const renderProvider = (provider: SocialProvider) => {
    const linked = identity.linkedProviders.includes(provider);
    const canUnlink = linked && identity.linkedProviders.length > 1;
    return (
      <div
        key={provider}
        className={`flex items-center justify-between gap-4 p-4 ${theme.row}`}
      >
        <div>
          <p className={`font-medium ${theme.text}`}>
            {providerLabel(provider)}
          </p>
          <p className={`text-sm ${theme.textMuted}`}>
            {linked ? t("account.connected") : t("account.notConnected")}
          </p>
        </div>
        {linked ? (
          <Button
            data-testid={`unlink-${provider}-btn`}
            type="button"
            variant="secondary"
            isDark={isDark}
            disabled={!canUnlink || pending !== null}
            className="w-auto px-4 py-2"
            onClick={() => void run(provider, () => unlinkProvider(provider))}
          >
            {t("account.unlink")}
          </Button>
        ) : (
          <Button
            data-testid={`connect-${provider}-btn`}
            type="button"
            variant="secondary"
            isDark={isDark}
            disabled={pending !== null}
            className="w-auto px-4 py-2"
            onClick={() => void connect(provider)}
          >
            {t("account.connect")}
          </Button>
        )}
      </div>
    );
  };

  const renderUsernameRow = () => {
    if (identity.provider !== "username") return null;
    return (
      <div
        className={`flex items-center justify-between gap-4 p-4 ${theme.row}`}
      >
        <div>
          <p className={`font-medium ${theme.text}`}>
            {t("account.username.title")}
          </p>
          <p className={`text-sm ${theme.textMuted}`}>
            {t("account.connected")}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen ${theme.bg}`}>
      <header className="flex items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        <HomeLink />
        <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
      </header>
      <main className="mx-auto max-w-2xl px-4 py-12">
        <h1 className={`text-4xl font-light tracking-tight ${theme.text}`}>
          {t("account.title")}
        </h1>
        <p className={`mt-3 ${theme.textMuted}`}>{t("account.subtitle")}</p>

        <section
          className={`mt-10 p-6 ${theme.panel}`}
        >
          <p className={`text-sm uppercase tracking-wider ${theme.textMuted}`}>
            {t("account.displayName")}
          </p>
          <div className="mt-2 flex items-center gap-3">
            <ProfileAvatar image={identity.image} name={identity.name} />
            <p data-testid="account-name" className={`text-2xl ${theme.text}`}>
              {identity.name}
            </p>
            <span data-testid="account-provider-badge">
              <ProviderBadge provider={identity.provider} />
            </span>
          </div>
          <p className={`mt-1 text-sm ${theme.textMuted}`}>
            {identity.provider === "username"
              ? t("account.username.nameHelp")
              : t("account.providerNameHelp")}
          </p>
        </section>

        <section className="mt-6 space-y-3">
          <h2 className={`text-xl ${theme.text}`}>
            {t("account.connections")}
          </h2>
          {renderUsernameRow()}
          {renderProvider("twitch")}
          {renderProvider("discord")}
        </section>

        {error && (
          <p
            data-testid="account-error"
            role="alert"
            className={`mt-6 ${theme.errorText}`}
          >
            {error}
          </p>
        )}

        <section className={`mt-10 border-t-2 pt-8 ${theme.divider}`}>
          {!identity.isAnonymous && (
            <Button
              data-testid="sign-out-btn"
              type="button"
              variant="secondary"
              isDark={isDark}
              disabled={pending !== null}
              onClick={() => {
                navigate("/");
                void run("sign-out", signOutToGuest);
              }}
            >
              {t("account.signOut")}
            </Button>
          )}

          <div className="mt-6">
            {confirmDelete ? (
              <div className={`p-4 ${theme.row}`}>
                <p className={theme.errorText}>{t("account.deleteWarning")}</p>
                <div className="mt-4 flex gap-3">
                  <Button
                    data-testid="confirm-delete-account-btn"
                    type="button"
                    variant="secondary"
                    isDark={isDark}
                    disabled={pending !== null}
                    className="w-auto px-4 py-2"
                    onClick={() => void confirmAccountDeletion()}
                  >
                    {t("account.confirmDelete")}
                  </Button>
                  <Button
                    data-testid="cancel-delete-account-btn"
                    type="button"
                    variant="secondary"
                    isDark={isDark}
                    className="w-auto px-4 py-2"
                    onClick={() => setConfirmDelete(false)}
                  >
                    {t("account.cancel")}
                  </Button>
                </div>
              </div>
            ) : (
              <button
                data-testid="delete-account-btn"
                type="button"
                className={`text-sm underline ${theme.errorText}`}
                onClick={() => setConfirmDelete(true)}
              >
                {t("account.delete")}
              </button>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
